import { useState } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { addMockCompany } from '@mocks/firmalar';

const COMPANY_TYPE_LABELS = {
    supplier: 'Tedarikçi',
    subcontractor: 'Taşeron',
    service: 'Hizmet',
};

/**
 * CompanySelector — Firma Seçici Dropdown
 *
 * Mevcut firmaları listeler. "Yeni Firma Ekle" seçilirse inline mini form açılır.
 *
 * Props:
 *   value: { id, name, _isNew? } | null
 *   onChange: (company: { id, name, type, _isNew? }) => void
 *   companies: array of { id, name, shortName?, type? }
 *   companyType: 'supplier' | 'subcontractor' | 'service'  (default: 'supplier')
 *   label: string
 *   required: boolean
 *   disabled: boolean
 *   placeholder: string
 *   className: string
 */
export default function CompanySelector({
    value,
    onChange,
    companies = [],
    companyType = 'supplier',
    label = 'Tedarikçi / Firma',
    required = false,
    disabled = false,
    placeholder = 'Firma seçin...',
    className = '',
}) {
    const [showNewForm, setShowNewForm] = useState(false);
    const [newName, setNewName] = useState('');

    const inputClass = `w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary
        focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}`;

    const handleSelectChange = (e) => {
        const val = e.target.value;
        if (val === '__new__') {
            setShowNewForm(true);
            return;
        }
        if (!val) {
            onChange(null);
            return;
        }
        const found = companies.find(c => c.id === val);
        if (found) onChange({ id: found.id, name: found.shortName || found.name, type: found.type });
    };

    const handleAddNew = () => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        // addMockCompany deduplication + push — backend: fn_upsert_company_account
        const tempId = `comp-new-${Date.now()}`;
        const typeLabel = COMPANY_TYPE_LABELS[companyType] || 'Tedarikçi';
        const newCompany = addMockCompany({ id: tempId, name: trimmed, shortName: trimmed, type: companyType, typeLabel, _isNew: true });
        onChange(newCompany);
        setShowNewForm(false);
        setNewName('');
    };

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                    {label} {required && <span className="text-error">*</span>}
                </label>
            )}

            {!showNewForm ? (
                <div className="relative">
                    <select
                        className={inputClass}
                        value={value?.id || ''}
                        onChange={handleSelectChange}
                        disabled={disabled}
                    >
                        <option value="">{placeholder}</option>
                        {companies.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.shortName || c.name}
                                {c.typeLabel ? ` (${c.typeLabel})` : ''}
                            </option>
                        ))}
                        <option value="__new__">+ Yeni Firma Ekle...</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                </div>
            ) : (
                <div className="flex gap-2">
                    <input
                        type="text"
                        autoFocus
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddNew(); if (e.key === 'Escape') setShowNewForm(false); }}
                        placeholder="Firma adını girin..."
                        className="flex-1 px-3 py-2.5 bg-bg-primary border border-primary/50 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <button
                        type="button"
                        onClick={handleAddNew}
                        disabled={!newName.trim()}
                        className="px-3 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                    >
                        <Plus size={14} /> Ekle
                    </button>
                    <button
                        type="button"
                        onClick={() => { setShowNewForm(false); setNewName(''); }}
                        className="px-3 py-2.5 border border-border-light rounded-lg text-sm text-text-secondary hover:bg-bg-hover"
                    >
                        İptal
                    </button>
                </div>
            )}

            {value?._isNew && (
                <p className="text-xs text-warning mt-1">
                    Yeni firma — kaydedildiğinde otomatik oluşturulur.
                </p>
            )}
        </div>
    );
}
